import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useDataSource } from '@/src/renderer/lib/dataSource'
import { CommentThread, type CommentThreadActions } from './CommentThread'

export interface ProjectCommentsProps {
  projectId: string
  /** Der angefangene Kommentar — er gehört dem Projekt (siehe `TaskDetails`). */
  draft: string
  onDraftChange: (value: string) => void
  /** Escape im Eingabefeld: die Tastatur zurück an die Liste. */
  onReleaseFocus?: () => void
}

/**
 * **Der Reiter „Kommentare" am Projekt** (#184) — dieselbe Kommentar-Liste (`CommentThread`) wie an
 * einer Aufgabe, in den Aufgaben-Details ohne Auswahl.
 *
 * @example
 * <ProjectComments projectId={project.id} draft={draft} onDraftChange={setDraft} onReleaseFocus={focusList} />
 */
export const ProjectComments: React.FC<ProjectCommentsProps> = ({ projectId, ...props }) => {
  const { t } = useTranslation()
  const source = useDataSource()
  const comments = source.useProjectComments(projectId)
  const actions = useMemo<CommentThreadActions>(() => ({
    create: content => source.createProjectComment(projectId, { content }),
    update: (id, content) => source.updateProjectComment(id, content),
    remove: id => source.deleteProjectComment(id),
    restore: id => source.restoreProjectComment(id),
  }), [source, projectId])

  return (
    <CommentThread
      {...props}
      comments={comments}
      actions={actions}
      ownerKey={`project:${projectId}`}
      emptyText={t('projectComments.empty')}
    />
  )
}
